import {
  Button,
  Dropdown,
  Flex,
  FormControl,
  HorizontalStack,
  IconButton,
  Input,
  Modal,
  Text,
} from '@lego/klik-ui';
import { MinusBold, PlusBold } from '@lego/klik-ui/icons';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeviceTypes } from '@/hooks/device';
import { type NetworkRow, useDeviceCreationForm } from '@/hooks/device/use_device_creation_form';
import { useWorkspaces } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
  workspaceId?: number;
  initialSerial?: string;
  initialVendor?: string;
  initialModel?: string;
  initialMacAddresses?: string[];
};

export const CreateDeviceDialog = ({
  isOpen,
  onClose,
  onCreated,
  workspaceId,
  initialSerial,
  initialVendor,
  initialModel,
  initialMacAddresses,
}: Props) => {
  const navigate = useNavigate();
  const form = useDeviceCreationForm({
    initialWorkspaceId: workspaceId,
    initialVendor,
    initialModel,
    initialSerial,
    initialNetworks: initialMacAddresses?.map(
      (macAddress): NetworkRow => ({
        id: crypto.randomUUID(),
        interface: '',
        macAddress,
      })
    ),
  });

  const { data: dataWorkspaces } = useWorkspaces();
  const { data: dataDeviceTypes } = useDeviceTypes();

  useEffect(() => {
    if (isOpen) {
      form.resetForm({
        initialWorkspaceId: workspaceId,
        initialVendor,
        initialModel,
        initialSerial,
        initialNetworks: initialMacAddresses?.map(
          (macAddress): NetworkRow => ({
            id: crypto.randomUUID(),
            interface: '',
            macAddress,
          })
        ),
      });
    }
  }, [
    isOpen,
    workspaceId,
    initialVendor,
    initialModel,
    initialSerial,
    initialMacAddresses,
    form.resetForm,
  ]);

  const onSubmit = async () => {
    const id = await form.handleSubmit();
    if (id) {
      onCreated?.();
      onClose();
      navigate(paths.deviceById(id));
    }
  };

  const workspaces = dataWorkspaces?.workspaces ?? [];
  const deviceTypes = dataDeviceTypes?.deviceTypes ?? [];
  const selectedWorkspace = workspaces.find((w) => w.id === form.workspaceId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      // Prevents the modal from setting aria-hidden on sibling portals,
      // which would hide Dropdown menus rendered in their own Chakra portal
      useInert={false}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Create a new device</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl isRequired>
            <FormControl.Label size="sm">Workspace</FormControl.Label>
            {workspaceId ? (
              <Input isDisabled value={selectedWorkspace?.name ?? ''} />
            ) : (
              <Dropdown
                onChange={(value: string) => {
                  const ws = workspaces.find((w) => w.name === value);
                  form.setWorkspaceId(ws?.id);
                }}
                value={selectedWorkspace ? [selectedWorkspace.name] : []}
              >
                {workspaces
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((ws) => (
                    <Dropdown.Option key={ws.id}>{ws.name}</Dropdown.Option>
                  ))}
              </Dropdown>
            )}
          </FormControl>
          <FormControl isRequired mt={4}>
            <FormControl.Label>Vendor</FormControl.Label>
            <Dropdown
              onChange={(value: string) => {
                form.changeVendor(value);
              }}
              value={form.vendor ? [form.vendor] : []}
            >
              {[...new Set(deviceTypes.map((dt) => dt.vendor))]
                .sort((a, b) => a.localeCompare(b))
                .map((v) => (
                  <Dropdown.Option key={v}>{v}</Dropdown.Option>
                ))}
            </Dropdown>
          </FormControl>
          <FormControl isRequired mt={4}>
            <FormControl.Label>Model</FormControl.Label>
            <Dropdown
              isDisabled={!form.vendor}
              onChange={(value: string) => form.setModel(value)}
              value={form.model ? [form.model] : []}
            >
              {deviceTypes
                .filter((dt) => dt.vendor === form.vendor)
                .sort((a, b) => a.model.localeCompare(b.model))
                .map((dt) => (
                  <Dropdown.Option key={dt.id}>{dt.model}</Dropdown.Option>
                ))}
            </Dropdown>
          </FormControl>
          <FormControl isInvalid={form.serialTooLong} isRequired mt={4}>
            <FormControl.Label>Serial</FormControl.Label>
            <Input
              onChange={(e) => form.setSerial(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void onSubmit();
              }}
              value={form.serial}
            />
            {form.serialTooLong && (
              <FormControl.ErrorText>Serial must be at most 100 characters</FormControl.ErrorText>
            )}
          </FormControl>
          <FormControl isInvalid={!form.hasAtLeastOneMac} isRequired mt={4}>
            <Flex alignContent="center" gap={2} mb={1}>
              <FormControl.Label m={0}>MAC Addresses</FormControl.Label>{' '}
              {!form.hasAtLeastOneMac && (
                <FormControl.ErrorText m={0}>
                  At least one MAC address is required
                </FormControl.ErrorText>
              )}
            </Flex>
            <FormControl.HelperText mb={2}>
              Format: 12 hex characters (e.g. AABBCCDDEEFF or AA:BB:CC:DD:EE:FF)
            </FormControl.HelperText>
            {form.networks.map((network, index) => (
              <FormControl isInvalid={!!form.macValidationErrors[index]} key={network.id} mb={2}>
                <Flex alignItems="center" gap={2}>
                  <Input
                    onChange={(e) =>
                      form.setNetworks(
                        form.networks.map((n, i) =>
                          i === index ? { ...n, macAddress: e.target.value } : n
                        )
                      )
                    }
                    placeholder="e.g. AA:BB:CC:DD:EE:FF"
                    value={network.macAddress}
                  />
                  {form.networks.length > 1 && (
                    <IconButton
                      aria-label="Remove MAC address"
                      icon={<MinusBold />}
                      onClick={() => form.setNetworks(form.networks.filter((_n, i) => i !== index))}
                      size="sm"
                      variant="ghost"
                    />
                  )}
                </Flex>
                {form.macValidationErrors[index] && (
                  <FormControl.ErrorText>{form.macValidationErrors[index]}</FormControl.ErrorText>
                )}
              </FormControl>
            ))}
            <Button
              leftIcon={<PlusBold />}
              mt={1}
              onClick={() =>
                form.setNetworks([
                  ...form.networks,
                  { id: crypto.randomUUID(), interface: '', macAddress: '' },
                ])
              }
              size="sm"
              variant="ghost"
            >
              Add another MAC address
            </Button>
          </FormControl>
          {form.error && (
            <Text color="error.500" fontSize="sm" mt={4}>
              {form.error}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button isDisabled={!form.canSubmit} isLoading={form.isSubmitting} onClick={onSubmit}>
              Create
            </Button>
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
